import { IconSearch } from '@developerz/ui';
import { createSignal, createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { PickerRepo } from '../lib/squads';
import { Field } from './Field';

// The squad modal's repo picker (#6780): a searchable checkbox list of every repo the
// one `GET /v1/squads` read carries. Presenter — the modal owns the selection.
//
// The search narrows what is SHOWN, never what is selected: a ticked repo the filter
// hides still counts in "<N> selected" and is still saved. A repo another squad holds
// is shown with that squad and cannot be ticked (#7010 D1: one squad per repo, never
// moved); its hint says how to free it.

export function SquadRepoPicker(props: {
  readonly repos: readonly PickerRepo[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (fullName: string) => void;
}): JSX.Element {
  const [query, setQuery] = createSignal('');
  const shown = (): readonly PickerRepo[] => {
    const q = query().trim().toLowerCase();
    return q === '' ? props.repos : props.repos.filter((r) => r.fullName.toLowerCase().includes(q));
  };

  return (
    <Field
      as="fieldset"
      label={t('squads.repos_label')}
      id="squad-repos"
      desc={t('squads.selected', { count: props.selected.size })}
    >
      {() => (
        <div class="squad-picker">
          <div class="search-input">
            <IconSearch size="1rem" />
            <input
              type="search"
              class="input"
              aria-label={t('squads.search_label')}
              placeholder={t('squads.search_label')}
              value={query()}
              onInput={(e) => setQuery(e.currentTarget.value)}
            />
          </div>
          <ul class="squad-picker__list">
            <For
              each={shown()}
              fallback={
                <li class="squad-picker__none">
                  {t(props.repos.length === 0 ? 'squads.no_repos' : 'squads.no_repos_match')}
                </li>
              }
            >
              {(repo) => {
                const hintId = createUniqueId();
                return (
                  <li class="squad-picker__repo">
                    <label class="squad-picker__check">
                      <input
                        type="checkbox"
                        checked={props.selected.has(repo.fullName)}
                        disabled={repo.heldBy !== null}
                        aria-describedby={repo.heldBy !== null ? hintId : undefined}
                        onChange={() => props.onToggle(repo.fullName)}
                      />
                      <code>{repo.fullName}</code>
                    </label>
                    <Show when={repo.heldBy}>
                      {(squad) => (
                        <>
                          <span class="badge badge--outline">
                            {t('squads.in_squad', { squad: squad() })}
                          </span>
                          <p id={hintId} class="squad-picker__hint">
                            {t('squads.in_squad_hint', { squad: squad() })}
                          </p>
                        </>
                      )}
                    </Show>
                  </li>
                );
              }}
            </For>
          </ul>
        </div>
      )}
    </Field>
  );
}
